import React, { useState } from 'react';
import { Eye, MoreVertical, Pencil, Trash2, Users } from 'lucide-react';
import { DIFFICULTY_TONES, categoryTone, statusInfo } from './courseDisplay';
import './AdminCourses.css';

/**
 * Table of courses on the admin Courses page. CourseListView renders it for
 * layout="table" and keeps owning every action (edit/delete/assign modals,
 * the "More" context menu and bulk selection).
 *
 * `progressByCourse` maps course id -> { studentsAssigned, averageProgress };
 * null while it is loading (or if it failed), which shows "—".
 */

function CourseThumb({ src, icon, title }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="acm-thumb">
      {src && !failed ? (
        <img src={src} alt={title} onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{icon || '📚'}</span>
      )}
    </span>
  );
}

function ProgressCell({ title, value }) {
  if (value == null) return <span className="acm-muted">—</span>;
  return (
    <div className="acm-progress">
      <div
        className="acm-progress-track"
        role="progressbar"
        aria-label={`${title} progress`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
      >
        <div
          className={`acm-progress-fill${value >= 100 ? ' is-complete' : ''}`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="acm-progress-value">{value}%</span>
    </div>
  );
}

export default function CourseTable({
  courses,
  loading,
  emptyTitle = 'No courses found',
  emptyHint = 'Click "Create New Course" to get started',
  progressByCourse,
  getThumbnailSrc,
  selectedCourseIds,
  onToggleCourse,
  onToggleAll,
  onView,
  onEdit,
  onDelete,
  onMore,
  actionLoading,
}) {
  const allSelected = courses.length > 0 && courses.every((course) => selectedCourseIds.includes(course._id));

  let body;
  if (loading) {
    body = (
      <tr>
        <td colSpan={12} className="acm-table-message">
          <span className="acm-spinner" aria-hidden="true" /> Loading courses…
        </td>
      </tr>
    );
  } else if (courses.length === 0) {
    body = (
      <tr>
        <td colSpan={12} className="acm-table-message">
          <strong>{emptyTitle}</strong>
          <span>{emptyHint}</span>
        </td>
      </tr>
    );
  } else {
    body = courses.map((course, index) => {
      const selected = selectedCourseIds.includes(course._id);
      const stats = progressByCourse ? progressByCourse[course._id] || { studentsAssigned: 0, averageProgress: 0 } : null;
      const status = statusInfo(course.status);
      const thumbnailSrc = getThumbnailSrc(course);

      return (
        <tr key={course._id} className={selected ? 'is-selected' : undefined}>
          <td className="acm-cell-check">
            <input
              type="checkbox"
              aria-label={`Select ${course.title}`}
              checked={selected}
              onChange={() => onToggleCourse(course._id)}
            />
          </td>
          <td className="acm-cell-num">{index + 1}</td>
          <td>
            <div className="acm-course">
              <CourseThumb key={thumbnailSrc} src={thumbnailSrc} icon={course.icon} title={course.title} />
              <div className="acm-course-text">
                <div className="acm-course-title" title={course.title}>{course.title}</div>
                {course.description && (
                  <div className="acm-course-desc" title={course.description}>{course.description}</div>
                )}
              </div>
            </div>
          </td>
          <td>
            <span className={`acm-badge acm-badge--${categoryTone(course.category)}`}>{course.category || '—'}</span>
          </td>
          <td>
            <span className={`acm-badge acm-badge--${DIFFICULTY_TONES[course.difficultyLevel] || 'gray'}`}>
              {course.difficultyLevel || '—'}
            </span>
          </td>
          <td className="acm-cell-num">{course.moduleCount || 0}</td>
          <td className="acm-cell-num">{course.chapterCount || 0}</td>
          <td className="acm-cell-num">{course.contentItemCount || 0}</td>
          <td>
            <span className="acm-students">
              <Users size={15} aria-hidden="true" />
              {stats ? stats.studentsAssigned : '—'}
            </span>
          </td>
          <td>
            <ProgressCell title={course.title} value={stats ? stats.averageProgress : null} />
          </td>
          <td>
            <span className={`acm-badge acm-badge--${status.tone}`}>{status.label}</span>
          </td>
          <td>
            <div className="acm-actions">
              <button
                type="button"
                className="acm-action acm-action--view"
                aria-label={`View ${course.title}`}
                title="View course content"
                onClick={() => onView(course)}
              >
                <Eye size={15} />
              </button>
              <button
                type="button"
                className="acm-action acm-action--edit"
                aria-label={`Edit ${course.title}`}
                title="Edit course details"
                onClick={() => onEdit(course)}
                disabled={actionLoading}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                className="acm-action acm-action--delete"
                aria-label={`Delete ${course.title}`}
                title="Delete course"
                onClick={() => onDelete(course._id)}
                disabled={actionLoading}
              >
                <Trash2 size={14} />
              </button>
              <button
                type="button"
                className="acm-action acm-action--more"
                aria-label={`More actions for ${course.title}`}
                title="Publish, archive, duplicate and more"
                onClick={(event) => onMore(event, course._id)}
                disabled={actionLoading}
              >
                <MoreVertical size={16} />
              </button>
            </div>
          </td>
        </tr>
      );
    });
  }

  return (
    <div className="acm-table-scroll">
      <table className="acm-table">
        <colgroup>
          <col className="acm-col-check" />
          <col className="acm-col-sno" />
          <col />
          <col className="acm-col-category" />
          <col className="acm-col-difficulty" />
          <col className="acm-col-modules" />
          <col className="acm-col-chapters" />
          <col className="acm-col-items" />
          <col className="acm-col-students" />
          <col className="acm-col-progress" />
          <col className="acm-col-status" />
          <col className="acm-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th className="acm-cell-check">
              <input
                type="checkbox"
                aria-label="Select all courses"
                checked={allSelected}
                onChange={onToggleAll}
                disabled={loading || courses.length === 0}
              />
            </th>
            <th>S.No</th>
            <th>Course Name</th>
            <th>Category</th>
            <th>Difficulty</th>
            <th className="acm-cell-num">Modules</th>
            <th className="acm-cell-num">Chapters</th>
            <th className="acm-cell-num">Items</th>
            <th>Students Assigned</th>
            <th>Progress</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
